import React, { useEffect, useState, useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-scroll';
import vishwasImage from "../assests/vishwas-hero.png";

const roles = ['OFFROAD', 'RACING', 'ATHLETE', 'SPEAKER', 'CORPORATE'];

const Hero = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [activeRole, setActiveRole] = useState(0);
  const ref = useRef(null);

  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start'],
  });

  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '40%']);
  const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '15%']);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 15,
        y: (e.clientY / window.innerHeight - 0.5) * 15,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveRole((prev) => (prev + 1) % roles.length);
    }, 2500);
    return () => clearInterval(interval);
  }, []);

  const letterVariants = {
    hidden: { y: '110%', opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: 0.2 + i * 0.08,
        duration: 1,
        ease: [0.25, 1, 0.5, 1],
      },
    }),
  };

  const name = 'VISHWAS'.split('');

  return (
    <section
      id="home"
      ref={ref}
      className="relative min-h-screen w-full overflow-hidden bg-darker flex flex-col mt-10 justify-center py-12 lg:py-10"
    >
      {/* Background ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full opacity-20"
          style={{
            background: 'radial-gradient(circle, #E10600 0%, transparent 60%)',
            transform: `translate(calc(-50% + ${mousePos.x}px), calc(-50% + ${mousePos.y}px))`,
            transition: 'transform 0.4s ease-out',
          }}
        />
      </div>

      {/* Grid Pattern */}
      <div
        className="absolute inset-0 opacity-[0.025] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
          backgroundSize: '100px 100px',
        }}
      />

      {/* Background Typography */}
      <motion.div
        style={{ y: textY, opacity }}
        className="absolute inset-x-0 top-[10vh] flex justify-center items-center pointer-events-none z-0 px-4"
      >
        <div className="relative flex ">
          {name.map((letter, i) => (
            <motion.span
              key={i}
              custom={i}
              variants={letterVariants}
              initial="hidden"
              animate="visible"
              className="font-heading text-[22vw] leading-[0.8] tracking-tighter text-white/90 inline-block select-none"
              style={{
                textShadow: '0 0 60px rgba(255, 255, 255, 0.08)',
              }}
            >
              {letter}
            </motion.span>
          ))}
        </div>
      </motion.div>

      {/* Main Content Layout - 3 Column Side-by-Side */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 pt-20 lg:pt-28 pb-10 min-h-screen flex items-center"
      >
        <div className="w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-4 items-center">
          
          {/* LEFT COLUMN: Bio & Details */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            className="lg:col-span-3 flex flex-col justify-center space-y-6 order-2 lg:order-1 text-left"
          >
            <div>
              <p className="text-xs tracking-[0.2em] text-white/50 uppercase font-mono mb-2">
                (Vishwas S D)
              </p>
              <p className="text-sm lg:text-base text-white/80 leading-relaxed font-body">
                A motorsport athlete pushing limits on every circuit. Born to
                race, driven by passion, fueled by the relentless pursuit of
                speed.
              </p>
            </div>

            <div className="pt-2 border-t border-white/10 font-mono">
              
              <p className="text-xs text-white/70 mt-1">Bengaluru, IN</p>
            </div>
          </motion.div>

          {/* CENTER COLUMN: High-Quality Featured Image */}
          <motion.div
            style={{ y: imageY }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 1, ease: [0.25, 1, 0.5, 1] }}
            className="lg:col-span-6 flex justify-center order-1 lg:order-2 my-4 lg:my-0"
          >
            <div className="relative w-full max-w-[420px] lg:max-w-[460px]">
              {/* High-Res Image Display */}
              <div className="relative w-full aspect-[3/4] overflow-hidden rounded-lg shadow-2xl">
                <img
                  src={vishwasImage}
                  alt="Vishwas Motorsport"
                  className="w-full h-full object-cover object-top select-none filter contrast-[1.04] brightness-[1.02]"
                />
                {/* Subtle base gradient fade */}
                <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-darker to-transparent opacity-80" />
              </div>

              {/* Racing Badge '07' */}
              <motion.div
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1.5, duration: 0.8, type: 'spring' }}
                className="absolute -bottom-5 -right-4 lg:-right-8 w-24 h-24 lg:w-28 lg:h-28 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center shadow-xl"
              >
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="absolute inset-0 pointer-events-none"
                >
                  <svg className="w-full h-full" viewBox="0 0 100 100">
                    <defs>
                      <path
                        id="circlePath"
                        d="M 50, 50 m -38, 0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0"
                      />
                    </defs>
                    <text className="fill-white/80 text-[9px] tracking-[0.3em] font-mono uppercase">
                      <textPath href="#circlePath">
                        RACING • DRIVER • VISHWAS •{' '}
                      </textPath>
                    </text>
                  </svg>
                </motion.div>
                <span className="font-heading text-3xl lg:text-4xl text-white"></span>
              </motion.div>
            </div>
          </motion.div>

          {/* RIGHT COLUMN: Roles Rotator & Scroll CTA */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.4, duration: 0.8 }}
            className="lg:col-span-3 flex flex-col justify-center items-start lg:items-end space-y-8 order-3 lg:text-right"
          >
            {/* Rotating Role Label */}
            <div className="w-full flex flex-col items-start lg:items-end gap-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] tracking-[0.3em] text-white/40 uppercase font-mono">
                  Role Focus
                </span>
                <div className="w-6 h-[1px] bg-white/20" />
              </div>

              <div className="relative h-10 w-full max-w-[200px] overflow-hidden">
                {roles.map((role, i) => (
                  <motion.div
                    key={i}
                    initial={false}
                    animate={{
                      y: activeRole === i ? 0 : activeRole > i ? '-100%' : '100%',
                      opacity: activeRole === i ? 1 : 0,
                    }}
                    transition={{ duration: 0.6, ease: [0.25, 1, 0.5, 1] }}
                    className="absolute inset-0 flex items-center justify-start lg:justify-end"
                  >
                    <span className="font-heading text-2xl lg:text-3xl tracking-[0.15em] text-white">
                      {role}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Indicator Dots */}
              <div className="flex items-center gap-1.5 mt-1">
                {roles.map((_, i) => (
                  <div
                    key={i}
                    className={`h-[2px] transition-all duration-500 ${
                      activeRole === i ? 'w-6 bg-white' : 'w-1.5 bg-white/20'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Scroll CTA */}
            <div className="pt-4">
              <Link
                to="about"
                smooth
                duration={800}
                offset={-100}
                className="cursor-pointer group flex items-center gap-3"
              >
                <span className="text-xs tracking-[0.3em] text-white/60 uppercase group-hover:text-white transition-colors duration-300 font-mono">
                  Scroll Down
                </span>
                <motion.div
                  animate={{ y: [0, 4, 0] }}
                  transition={{ repeat: Infinity, duration: 1.5 }}
                  className="w-9 h-9 rounded-full border border-white/20 flex items-center justify-center group-hover:border-white transition-all duration-300"
                >
                  <span className="text-white/60 text-sm group-hover:text-white transition-colors duration-300">
                    ↓
                  </span>
                </motion.div>
              </Link>
            </div>
          </motion.div>

        </div>
      </motion.div>
    </section>
  );
};

export default Hero;